import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  ShoppingBag, BarChart3, Package, Users, Building2, Check, ArrowRight, 
  Smartphone, Cloud, Shield, Clock, Star, Menu, X, Home, Zap, Shirt, Gift, Globe,
  CreditCard, ShieldCheck, Printer, Wallet, FileCheck, QrCode, Boxes, ReceiptText,
  Send, Loader2, Mail, Phone
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function MockQR() {
  const size = 21;
  const finderCell = (row, col) => {
    const inBlock = (top, left) => row >= top && row < top + 7 && col >= left && col < left + 7;
    const block = [[0, 0], [0, size - 7], [size - 7, 0]].find(([top, left]) => inBlock(top, left));
    if (!block) return null;
    const localRow = row - block[0];
    const localCol = col - block[1];
    return localRow === 0 || localRow === 6 || localCol === 0 || localCol === 6 ||
      (localRow >= 2 && localRow <= 4 && localCol >= 2 && localCol <= 4);
  };
  const cells = [];
  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) {
      const finder = finderCell(row, col);
      const filled = finder === null ? (row * 7 + col * 5 + row * col) % 3 === 0 : finder;
      if (filled) cells.push(<rect key={`${row}-${col}`} x={col} y={row} width="1.02" height="1.02" fill="#0f172a" />);
    }
  }
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width="104" height="104" className="rounded-md bg-white p-1" shapeRendering="crispEdges" aria-label="Example fiscal QR code">
      {cells}
    </svg>
  );
}

function HeroReceipt() {
  return (
    <div className="relative mx-auto w-full max-w-xs hp-float">
      <div className="absolute -inset-6 bg-primary/20 blur-3xl rounded-full" aria-hidden="true" />
      <div className="relative bg-white rounded-xl shadow-2xl border border-gray-100 p-5 font-mono text-[11px] text-gray-700 overflow-hidden">
        <div className="hp-scan" aria-hidden="true" />
        <div className="text-center mb-3">
          <div className="mx-auto w-8 h-8 bg-primary rounded-lg flex items-center justify-center mb-2">
            <ShoppingBag className="h-5 w-5 text-primary-foreground" />
          </div>
          <p className="font-sans font-bold text-sm text-gray-900">Urban Edit</p>
          <p className="text-[10px] text-gray-400">TAX INVOICE • KRA eTIMS</p>
        </div>
        <div className="border-t border-dashed border-gray-200 my-2" />
        {[["1x Linen Shirt / M", "3,200"], ["1x Leather Belt", "1,450"], ["Loyalty discount", "-250"]].map(([label, amount]) => (
          <div key={label} className="flex justify-between py-0.5"><span>{label}</span><span>{amount}</span></div>
        ))}
        <div className="border-t border-dashed border-gray-200 my-2" />
        <div className="flex justify-between"><span>VAT (16%)</span><span>606.90</span></div>
        <div className="flex justify-between font-sans font-bold text-gray-900 text-xs mt-1"><span>TOTAL</span><span>KSh 4,400</span></div>
        <div className="border-t border-dashed border-gray-200 my-2" />
        <div className="flex items-center gap-3">
          <div className="hp-qr-in shrink-0"><MockQR /></div>
          <div className="text-[9px] leading-tight text-gray-500">
            <p className="font-semibold text-gray-700">SCAN TO VERIFY</p>
            <p>SDC: KRAMW009202401</p>
            <p>CU INV: 0010200034</p>
            <p className="text-primary font-semibold mt-1">Fiscalised with KRA</p>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes hp-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        .hp-float { animation: hp-float 5s ease-in-out infinite; }
        @keyframes hp-scan { 0% { top: -20%; } 100% { top: 120%; } }
        .hp-scan { position: absolute; left: 0; right: 0; height: 40px; background: linear-gradient(to bottom, transparent, rgba(34,197,94,0.18), transparent); animation: hp-scan 3.2s ease-in-out infinite; }
        @keyframes hp-qr-in { 0% { opacity: 0; transform: scale(0.85); } 100% { opacity: 1; transform: scale(1); } }
        .hp-qr-in { animation: hp-qr-in 700ms ease-out 400ms both; }
        @media (prefers-reduced-motion: reduce) { .hp-float, .hp-scan, .hp-qr-in { animation: none; } }
      `}</style>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [pricingConfig, setPricingConfig] = useState({
    annual_discount_months: 2,
    integration_price_per_property: 2500,
    currency: "KES"
  });
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contact, setContact] = useState({ name: "", business_name: "", email: "", phone: "", message: "" });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactDone, setContactDone] = useState(false);

  const submitContact = async (event) => {
    event.preventDefault();
    if (!contact.name.trim() || !contact.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    try {
      setContactSubmitting(true);
      const response = await axios.post(`${API_URL}/api/public/contact`, { ...contact, source: "retail-landing" });
      toast.success(response.data?.message || "Thanks! We'll be in touch shortly.");
      setContactDone(true);
      setContact({ name: "", business_name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not send your message. Please try again.");
    } finally {
      setContactSubmitting(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [plansRes, addonsRes, configRes] = await Promise.all([
          axios.get(`${API_URL}/api/public/plans`),
          axios.get(`${API_URL}/api/public/addons`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/public/pricing-config`).catch(() => ({ data: pricingConfig }))
        ]);
        setPlans(plansRes.data);
        setAddons(addonsRes.data || []);
        setPricingConfig(configRes.data || pricingConfig);
      } catch (error) {
      }
    };
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const features = [
    {
      icon: ShoppingBag,
      title: "Retail POS Built for Fashion",
      description: "Lightning-fast checkout with barcode scanning, size & colour variants, and split tenders."
    },
    {
      icon: Shirt,
      title: "Variant & Matrix Inventory",
      description: "Track every SKU by style, size, and colour across stores. Real-time stock and low-stock alerts."
    },
    {
      icon: BarChart3,
      title: "Real-time Reports",
      description: "Sales analytics, best-sellers, margins, and stock valuation across all your outlets."
    },
    {
      icon: Globe,
      title: "Built-in eCommerce",
      description: "Launch a branded online store per tenant. Sell in-store and online from one catalogue."
    },
    {
      icon: Gift,
      title: "Loyalty & Customer App",
      description: "Reward repeat shoppers with points and tiers, accessible from a customer mobile app."
    },
    {
      icon: Building2,
      title: "Multi-Property & Multi-Outlet",
      description: "Run multiple stores and brands under one tenant, each with its own catalogue and team."
    },
    {
      icon: ShieldCheck,
      title: "KRA eTIMS Ready",
      description: "Fiscalise sales and credit notes, print verifiable QR receipts, and reconcile KRA item and import records.",
      highlight: true
    },
    {
      icon: CreditCard,
      title: "Connected Payments",
      description: "Accept cash, M-Pesa and card payments, including split tenders and supported payment terminals."
    },
    {
      icon: Printer,
      title: "Retail Receipt Printing",
      description: "Print itemised fiscal receipts on iMin terminals, browser printers, or QZ-connected receipt printers."
    }
  ];

  const benefits = [
    { icon: Cloud, text: "Cloud-based - Access anywhere" },
    { icon: Shield, text: "Secure & Reliable" },
    { icon: Clock, text: "30-Day Free Trial" },
    { icon: Star, text: "Local Support in Kenya" }
  ];

  const etimsPoints = [
    { icon: ReceiptText, title: "Fiscal receipts + QR", text: "Completed sales are fiscalised and printed with KRA invoice details and a verification QR code." },
    { icon: QrCode, title: "Retail item sync", text: "Map products and variants to valid KRA classification, tax, packaging and quantity-unit codes." },
    { icon: FileCheck, title: "Credit notes", text: "Refunds retain the original sale reference and generate a dedicated fiscal credit-note receipt." },
    { icon: Boxes, title: "Import reconciliation", text: "Review KRA import declarations, match retail items and approve stock into the correct store." },
    { icon: Package, title: "Purchases and GRNs", text: "Reconcile supplier invoices and submit received stock as compliant purchase and stock records." },
    { icon: BarChart3, title: "Audit visibility", text: "Monitor submissions, errors, retries, stock movements and certification progress from one workspace." }
  ];

  const integrations = [
    { icon: Wallet, name: "M-Pesa", tag: "STK Push, C2B and reference capture" },
    { icon: CreditCard, name: "Pesapal Cloud POS", tag: "Card terminal and M-Pesa payments" },
    { icon: CreditCard, name: "DPO / Network", tag: "Supported terminal push-to-pay flows" },
    { icon: FileCheck, name: "KRA eTIMS", tag: "Fiscal sales, notes, items and stock" },
    { icon: Printer, name: "iMin Terminals", tag: "Built-in thermal receipt printing" },
    { icon: Printer, name: "QZ Tray", tag: "Windows and USB direct printing" },
    { icon: Globe, name: "Online Store", tag: "One catalogue for store and web orders" },
    { icon: Gift, name: "Loyalty", tag: "Points, tiers and customer rewards" }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', maximumFractionDigits: 0 }).format(amount);
  };

  // Helper to check if plan has unlimited/negotiable pricing
  const isUnlimited = (value) => !value || value === 0 || value >= 999;
  const isNegotiablePlan = (plan) => isUnlimited(plan.max_properties) && isUnlimited(plan.max_outlets) && isUnlimited(plan.max_users);

  // Format limit display
  const formatLimit = (value, singular, plural) => {
    if (isUnlimited(value)) return "Unlimited";
    return `${value} ${value > 1 ? plural : singular}`;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <ShoppingBag className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold text-gray-900">HostPoint <span className="text-primary">Retail</span></span>
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              <a href="#features" className="text-gray-600 hover:text-gray-900 text-sm font-medium">Features</a>
              <a href="#compliance" className="text-gray-600 hover:text-gray-900 text-sm font-medium">eTIMS</a>
              <a href="#integrations" className="text-gray-600 hover:text-gray-900 text-sm font-medium">Integrations</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900 text-sm font-medium">Pricing</a>
              <Button variant="outline" onClick={() => navigate("/login")}>Sign In</Button>
              <Button onClick={() => navigate("/register")}>Get Started</Button>
            </div>

            {/* Mobile menu button */}
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t p-4 space-y-4">
            <a href="#features" className="block text-gray-600 hover:text-gray-900 font-medium" onClick={() => setMobileMenuOpen(false)}>Features</a>
            <a href="#compliance" className="block text-gray-600 hover:text-gray-900 font-medium" onClick={() => setMobileMenuOpen(false)}>eTIMS Compliance</a>
            <a href="#integrations" className="block text-gray-600 hover:text-gray-900 font-medium" onClick={() => setMobileMenuOpen(false)}>Integrations</a>
            <a href="#pricing" className="block text-gray-600 hover:text-gray-900 font-medium" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
            <a href="#contact" className="block text-gray-600 hover:text-gray-900 font-medium" onClick={() => setMobileMenuOpen(false)}>Contact</a>
            <Button variant="outline" className="w-full" onClick={() => navigate("/login")}>Sign In</Button>
            <Button className="w-full" onClick={() => navigate("/register")}>Get Started</Button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left max-w-2xl mx-auto lg:mx-0">
            <Badge variant="secondary" className="mb-4">
              Built for Kenya&apos;s fashion and lifestyle retailers
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-6">
              Retail POS, eCommerce and loyalty — KRA eTIMS ready
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-8">
              Sell in store and online, manage every size and colour, reward loyal shoppers,
              and issue verifiable KRA fiscal receipts across every outlet.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button size="lg" onClick={() => navigate("/register")} className="text-base">
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/login")} className="text-base">
                Sign In to Dashboard
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              30-day free trial • No credit card required • Cancel anytime
            </p>
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 mt-8">
              {["KRA eTIMS", "M-Pesa", "Retail variants", "Online store", "Loyalty"].map((label) => (
                <span key={label} className="inline-flex items-center gap-1.5 rounded-full border bg-white px-3 py-1 text-xs font-medium text-gray-700">
                  <Check className="h-3.5 w-3.5 text-primary" /> {label}
                </span>
              ))}
            </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <HeroReceipt />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Bar */}
      <section className="py-8 bg-gray-50 border-y">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {benefits.map((benefit) => (
              <div key={benefit.text} className="flex items-center justify-center gap-2 text-gray-700">
                <benefit.icon className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Everything You Need</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From quick checkout to variant inventory, online storefronts, fiscal compliance, and loyalty — HostPoint Retail
              has everything your fashion business needs.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => (
              <Card key={feature.title} className={`border-0 shadow-sm hover:shadow-md transition-shadow ${feature.highlight ? 'ring-1 ring-primary/30 bg-primary/5' : ''}`}>
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    {feature.title}
                    {feature.highlight && <Badge className="bg-primary text-[10px]">Compliance</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* eTIMS Compliance Section */}
      <section id="compliance" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-950 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <Badge className="bg-primary mb-4"><ShieldCheck className="h-3.5 w-3.5 mr-1" /> KRA eTIMS integration</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Compliance built into retail checkout</h2>
              <p className="text-gray-300 mb-6 max-w-xl">
                HostPoint Retail connects sales, returns, products, purchases and stock movements to eTIMS.
                Fiscalisation happens as part of checkout, while failed submissions remain visible and safely retryable
                without recording the customer&apos;s payment twice.
              </p>
              <ul className="space-y-3">
                {["Preflight checks before settlement", "Persisted fiscal receipts and credit-note reprints", "Per-outlet credentials, transaction history and error recovery"].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-gray-200">
                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <Button className="mt-8" onClick={() => navigate("/register")}>Get eTIMS-ready <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {etimsPoints.map((point) => (
                <div key={point.title} className="rounded-xl bg-white/5 border border-white/10 p-5">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center mb-3"><point.icon className="h-5 w-5 text-primary" /></div>
                  <h3 className="font-semibold text-sm mb-1">{point.title}</h3>
                  <p className="text-xs text-gray-400">{point.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Payments & Integrations Section */}
      <section id="integrations" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Payments, devices and retail channels</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Connect the payment rails, fiscal service, printers and sales channels your stores use every day.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {integrations.map((integration) => (
              <Card key={integration.name} className="border shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
                <CardContent className="pt-6 pb-5">
                  <div className="w-11 h-11 bg-primary/10 rounded-lg flex items-center justify-center mb-3"><integration.icon className="h-5 w-5 text-primary" /></div>
                  <h3 className="font-semibold text-sm mb-1 text-gray-900">{integration.name}</h3>
                  <p className="text-xs text-gray-500">{integration.tag}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-gray-600 max-w-2xl mx-auto mb-6">
              Choose the plan that fits your business. All plans include a 30-day free trial.
              Pay annually and get <span className="font-semibold text-primary">{pricingConfig.annual_discount_months} months FREE</span>!
            </p>
            
            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <Tabs value={billingCycle} onValueChange={setBillingCycle} className="w-auto">
                <TabsList className="grid grid-cols-2 w-64">
                  <TabsTrigger value="monthly">Monthly</TabsTrigger>
                  <TabsTrigger value="annually" className="relative">
                    Annually
                    <Badge className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0.5 bg-green-500">Save {pricingConfig.annual_discount_months} mo</Badge>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>

          {/* Responsive grid: 1 col mobile, 2 cols tablet, 3-4 cols desktop based on plan count */}
          <div className={`grid gap-6 max-w-7xl mx-auto px-4 ${
            plans.length <= 3 
              ? 'md:grid-cols-2 lg:grid-cols-3' 
              : plans.length === 4 
                ? 'md:grid-cols-2 lg:grid-cols-4' 
                : 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
          }`}>
            {plans.map((plan) => (
              <Card 
                key={plan.id} 
                className={`relative flex flex-col ${plan.code === 'pro' || plan.name.toLowerCase().includes('professional') ? 'border-primary shadow-lg ring-2 ring-primary' : isNegotiablePlan(plan) ? 'border-amber-400 bg-gradient-to-b from-amber-50 to-white' : 'border-gray-200'}`}
              >
                {(plan.code === 'pro' || plan.name.toLowerCase().includes('professional')) && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary">Most Popular</Badge>
                  </div>
                )}
                {isNegotiablePlan(plan) && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-amber-500">Enterprise</Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <CardDescription className="min-h-[40px] text-xs">{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="mb-4">
                    {isNegotiablePlan(plan) ? (
                      <>
                        <span className="text-2xl font-bold text-amber-600">Negotiable</span>
                        <div className="text-xs text-gray-500 mt-1">Contact us for pricing</div>
                      </>
                    ) : (
                      <>
                        <span className="text-3xl font-bold text-gray-900">
                          {formatCurrency(billingCycle === "monthly" ? plan.price_monthly : Math.round(plan.price_yearly / 12))}
                        </span>
                        <span className="text-gray-500">/mo</span>
                        {billingCycle === "annually" && (
                          <div className="text-xs text-green-600 mt-1">
                            Billed {formatCurrency(plan.price_yearly)}/year
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  
                  <ul className="space-y-2 text-left text-sm">
                    <li className="flex items-center gap-2">
                      <Home className="h-4 w-4 text-primary flex-shrink-0" />
                      <span>{formatLimit(plan.max_properties, 'Property', 'Properties')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
                      <span>{formatLimit(plan.max_outlets, 'Outlet', 'Outlets')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-primary flex-shrink-0" />
                      <span>{formatLimit(plan.max_users, 'User', 'Users')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span>Point of Sale</span>
                    </li>
                    {plan.includes_inventory && (
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span>Inventory Management</span>
                      </li>
                    )}
                    {plan.includes_reports && (
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span>Reports & Analytics</span>
                      </li>
                    )}
                    {plan.is_group_plan && (
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span>Group Dashboard</span>
                      </li>
                    )}
                  </ul>
                </CardContent>
                <CardFooter className="mt-auto">
                  <Button 
                    className="w-full" 
                    variant={(plan.code === 'pro' || plan.name.toLowerCase().includes('professional')) ? 'default' : isNegotiablePlan(plan) ? 'default' : 'outline'}
                    onClick={() => navigate(`/register?plan=${plan.code}`)}
                  >
                    {isNegotiablePlan(plan) ? 'Contact Sales' : 'Start Free Trial'}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* Integration Add-ons */}
          {addons.length > 0 && (
            <div className="mt-16">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Integration Add-ons</h3>
                <p className="text-gray-600">
                  Powerful integrations billed per property per month
                </p>
              </div>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 max-w-6xl mx-auto">
                {addons.map((addon) => (
                  <Card key={addon.id} className={`text-center ${addon.is_coming_soon ? 'opacity-60' : ''}`}>
                    <CardContent className="pt-6 pb-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <Zap className="h-5 w-5 text-primary" />
                      </div>
                      <h4 className="font-medium text-sm mb-1">{addon.name}</h4>
                      {addon.is_coming_soon ? (
                        <Badge variant="secondary" className="text-xs">Coming Soon</Badge>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(addon.price_per_property)}/property/mo
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          <p className="text-center text-sm text-gray-500 mt-8">
            All prices in Kenya Shillings (KES). Pay via M-Pesa or Card. Annual billing = {12 - pricingConfig.annual_discount_months} months paid upfront.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Business?</h2>
          <p className="text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
            Join fashion and lifestyle retailers already using HostPoint Retail to unify their
            in-store, online, and loyalty operations.
          </p>
          <Button size="lg" variant="secondary" onClick={() => navigate("/register")}>
            Start Your 30-Day Free Trial
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Contact Sales Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 border-t">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Talk to our retail team</h2>
            <p className="text-gray-600 mb-8">
              Planning multiple stores, moving from another POS, or preparing for KRA eTIMS?
              Tell us what your retail business needs and our team will get back to you.
            </p>
            <ul className="space-y-4 text-sm">
              <li className="flex items-center gap-3 text-gray-700">
                <span className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Mail className="h-5 w-5 text-primary" /></span>
                <a href="mailto:support@hostpoint.co.ke" className="hover:text-primary">support@hostpoint.co.ke</a>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <span className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Phone className="h-5 w-5 text-primary" /></span>
                <a href="tel:+254700000000" className="hover:text-primary">+254 700 000 000</a>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <span className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Building2 className="h-5 w-5 text-primary" /></span>
                <span>Nairobi, Kenya</span>
              </li>
            </ul>
          </div>
          <Card className="shadow-lg border-0">
            <CardContent className="pt-6">
              {contactDone ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4"><Check className="h-7 w-7 text-primary" /></div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Message sent</h3>
                  <p className="text-sm text-gray-600 mb-6">Thanks for reaching out. Our team will be in touch shortly.</p>
                  <Button variant="outline" onClick={() => setContactDone(false)}>Send another message</Button>
                </div>
              ) : (
                <form onSubmit={submitContact} className="space-y-4" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label htmlFor="contact-name">Name *</Label><Input id="contact-name" value={contact.name} onChange={(event) => setContact({ ...contact, name: event.target.value })} placeholder="Your name" className="mt-1" required /></div>
                    <div><Label htmlFor="contact-business">Business</Label><Input id="contact-business" value={contact.business_name} onChange={(event) => setContact({ ...contact, business_name: event.target.value })} placeholder="Retail business name" className="mt-1" /></div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label htmlFor="contact-email">Email *</Label><Input id="contact-email" type="email" value={contact.email} onChange={(event) => setContact({ ...contact, email: event.target.value })} placeholder="you@business.co.ke" className="mt-1" required /></div>
                    <div><Label htmlFor="contact-phone">Phone</Label><Input id="contact-phone" value={contact.phone} onChange={(event) => setContact({ ...contact, phone: event.target.value })} placeholder="07xx xxx xxx" className="mt-1" /></div>
                  </div>
                  <div><Label htmlFor="contact-message">How can we help?</Label><Textarea id="contact-message" value={contact.message} onChange={(event) => setContact({ ...contact, message: event.target.value })} placeholder="Tell us about your stores and what you need…" className="mt-1" rows={4} /></div>
                  <Button type="submit" className="w-full" disabled={contactSubmitting}>
                    {contactSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : <><Send className="mr-2 h-4 w-4" /> Send message</>}
                  </Button>
                  <p className="text-xs text-gray-400 text-center">We only use your details to respond to your enquiry.</p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <ShoppingBag className="h-5 w-5 text-primary-foreground" />
                </div>
                <span className="text-xl font-bold text-white">HostPoint Retail</span>
              </div>
              <p className="text-sm">
                Complete Retail POS, eCommerce, and Loyalty platform for fashion & lifestyle stores.
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                <li><a href="#compliance" className="hover:text-white">eTIMS Compliance</a></li>
                <li><a href="#integrations" className="hover:text-white">Integrations</a></li>
                <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/help" className="hover:text-white">Help Center</Link></li>
                <li><a href="#contact" className="hover:text-white">Contact Us</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Platform</h4>
              <ul className="space-y-2 text-sm">
                <li>Online storefront</li>
                <li>Customer loyalty</li>
                <li>Mobile POS support</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li>Email: support@hostpoint.co.ke</li>
                <li>Phone: +254 700 000 000</li>
                <li>Nairobi, Kenya</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 text-sm text-center">
            <p>© {new Date().getFullYear()} HostPoint. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
